import React from 'react'

interface FrostOverlayProps {
  /** CTA label shown under the monogram, e.g. "VIEW LISTING" */
  cta: string
  /** Suppress the centered duplicate CTA chip (keep blur + monogram) when the
   *  host card already shows its own real CTA link on hover. */
  hideCta?: boolean
  /** When provided, the CTA chip itself is the card's real link (clickable
   *  through the pointer-events-none frost layers). */
  href?: string
  /** Where the brand image sits. 'center' (default) for photo cards whose
   *  text lives at the bottom; 'top-right'/'top-left' for cards with
   *  bottom-anchored copy so the monogram never overlaps it. */
  monogramPos?: 'center' | 'top-right' | 'top-left'
  /** Where the static touch-device CTA chip sits. Default 'bottom-right';
   *  use 'top-right' when the card's bottom is occupied by always-visible copy. */
  touchCtaPos?: 'bottom-right' | 'top-right'
  /** Also show a small static brand image (top-left) on touch devices, where
   *  the hover frost/monogram layers are hidden. */
  touchMonogram?: boolean
  /** Suppress the static touch-device corner chip entirely (host renders its
   *  own touch CTA, e.g. a strip below the photo). */
  hideTouchCta?: boolean
}

/**
 * Frosted-glass brand hover overlay (Parity V2 A.4).
 * Renders inside any `group relative overflow-hidden` photo card.
 * Desktop: frost (blur + ink tint) fades in on hover with social brand image + CTA.
 * Touch (hover: none): static CTA chip bottom-right, no frost.
 */
const FrostOverlay: React.FC<FrostOverlayProps> = ({
  cta,
  hideCta = false,
  href,
  monogramPos = 'center',
  touchCtaPos = 'bottom-right',
  touchMonogram = false,
  hideTouchCta = false,
}) => {
  const external = href ? !href.startsWith('/') : false
  const topRight = monogramPos === 'top-right'
  const topLeft = monogramPos === 'top-left'
  const corner = topRight || topLeft
  return (
    <>
      {/* Frost layer — hover-capable devices only */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-10 bg-primary-ink/30 backdrop-blur-[6px] opacity-0 group-hover:opacity-100 transition-opacity duration-[250ms] [@media(hover:none)]:hidden"
      />

      {/* Branding + CTA — hover-capable devices only.
          Centered by default; top-right when bottom text needs the middle clear. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute z-20 flex opacity-0 group-hover:opacity-100 transition-opacity duration-[250ms] [@media(hover:none)]:hidden ${
          corner
            ? `inset-x-0 top-0 flex-row items-start p-5 ${topLeft ? 'justify-start' : 'justify-end'}`
            : 'inset-0 flex-col items-center justify-center gap-3 pb-24'
        }`}
      >
        <img
          src="/media/team/merritt-shawn-profile.jpg"
          alt=""
          width={320}
          height={320}
          className={`rounded-full border border-white/70 object-cover drop-shadow ${corner ? 'h-8 w-8' : 'h-14 w-14'}`}
        />
        {!hideCta &&
          (href ? (
            <a
              href={href}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="pointer-events-auto bg-primary-ink/30 px-3 py-1 font-display text-[12px] font-medium uppercase tracking-[0.25em] text-white hover:bg-accent-gold transition-colors duration-300"
            >
              {cta} +
            </a>
          ) : (
            <span className="bg-primary-ink/30 px-3 py-1 font-display text-[12px] font-medium uppercase tracking-[0.25em] text-white">
              {cta} +
            </span>
          ))}
      </div>

      {/* Static social brand image — touch devices only (opt-in) */}
      {touchMonogram && (
        <img
          src="/media/team/merritt-shawn-profile.jpg"
          alt=""
          aria-hidden="true"
          width={320}
          height={320}
          className="pointer-events-none absolute top-4 left-4 z-20 hidden h-8 w-8 rounded-full border border-white/70 object-cover drop-shadow [@media(hover:none)]:block"
        />
      )}

      {/* Static CTA chip — touch devices only */}
      {!hideTouchCta && (
      <div
        {...(href ? {} : { 'aria-hidden': true })}
        className={`pointer-events-none absolute z-20 hidden [@media(hover:none)]:block ${
          touchCtaPos === 'top-right' ? 'top-4 right-4' : 'bottom-3 right-2'
        }`}
      >
        {href ? (
          <a
            href={href}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="pointer-events-auto inline-block bg-primary-ink/70 px-2 py-0.5 font-display text-[9px] font-medium uppercase tracking-[0.15em] text-white"
          >
            {cta} +
          </a>
        ) : (
          <span className="inline-block bg-primary-ink/70 px-2 py-0.5 font-display text-[9px] font-medium uppercase tracking-[0.15em] text-white">
            {cta} +
          </span>
        )}
      </div>
      )}
    </>
  )
}

export default FrostOverlay
